import { useEffect } from 'react';

/**
 * i18n_ — SEO page meta hook.
 *
 * Sets document.title and the meta description, Open Graph and Twitter
 * card tags for the current page. Pages call it once with their static
 * metadata; the effect cleans up the `<meta>` nodes it created on unmount
 * so the next page starts with a blank slate.
 *
 * Intentionally does not modify `<meta name="viewport">` or other global
 * tags — it only touches tags keyed by `name` / `property` from this
 * options object.
 */
export interface PageMeta {
  title: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  robots?: string;
}

const DEFAULT_IMAGE = 'https://aiads.trustluna.com/pwa-512.png';
const SITE_BASE = 'https://aiads.trustluna.com';

function upsertMeta(selector: string, attrs: Record<string, string>): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    for (const [k, v] of Object.entries(attrs)) {
      el.setAttribute(k, v);
    }
    document.head.appendChild(el);
  }
  return el;
}

export function usePageMeta(meta: PageMeta): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = meta.title;

    const description =
      meta.description ??
      'AIAds — Google Ads management platform with AI copilot for agencies.';
    const image = meta.image ?? DEFAULT_IMAGE;
    const url = meta.url ?? (typeof window !== 'undefined' ? window.location.href : SITE_BASE);
    const type = meta.type ?? 'website';

    // Standard meta description
    const metaDesc = upsertMeta('meta[name="description"]', { name: 'description' });
    const prevDesc = metaDesc.getAttribute('content');
    metaDesc.setAttribute('content', description);

    // Open Graph
    const ogTitle = upsertMeta('meta[property="og:title"]', { property: 'og:title' });
    const prevOgTitle = ogTitle.getAttribute('content');
    ogTitle.setAttribute('content', meta.title);

    const ogDesc = upsertMeta('meta[property="og:description"]', { property: 'og:description' });
    const prevOgDesc = ogDesc.getAttribute('content');
    ogDesc.setAttribute('content', description);

    const ogImage = upsertMeta('meta[property="og:image"]', { property: 'og:image' });
    const prevOgImage = ogImage.getAttribute('content');
    ogImage.setAttribute('content', image);

    const ogType = upsertMeta('meta[property="og:type"]', { property: 'og:type' });
    const prevOgType = ogType.getAttribute('content');
    ogType.setAttribute('content', type);

    const ogUrl = upsertMeta('meta[property="og:url"]', { property: 'og:url' });
    const prevOgUrl = ogUrl.getAttribute('content');
    ogUrl.setAttribute('content', url);

    // Twitter card
    const twCard = upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' });
    const prevTwCard = twCard.getAttribute('content');
    twCard.setAttribute('content', 'summary_large_image');

    const twTitle = upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' });
    const prevTwTitle = twTitle.getAttribute('content');
    twTitle.setAttribute('content', meta.title);

    const twDesc = upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' });
    const prevTwDesc = twDesc.getAttribute('content');
    twDesc.setAttribute('content', description);

    const twImage = upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' });
    const prevTwImage = twImage.getAttribute('content');
    twImage.setAttribute('content', image);

    // Robots directive (useful for public pages that allow indexing)
    let metaRobots: HTMLMetaElement | null = null;
    let prevRobots: string | null = null;
    if (meta.robots) {
      metaRobots = upsertMeta('meta[name="robots"]', { name: 'robots' });
      prevRobots = metaRobots.getAttribute('content');
      metaRobots.setAttribute('content', meta.robots);
    }

    return () => {
      document.title = previousTitle;
      if (prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
      if (prevOgTitle !== null) ogTitle.setAttribute('content', prevOgTitle);
      if (prevOgDesc !== null) ogDesc.setAttribute('content', prevOgDesc);
      if (prevOgImage !== null) ogImage.setAttribute('content', prevOgImage);
      if (prevOgType !== null) ogType.setAttribute('content', prevOgType);
      if (prevOgUrl !== null) ogUrl.setAttribute('content', prevOgUrl);
      if (prevTwCard !== null) twCard.setAttribute('content', prevTwCard);
      if (prevTwTitle !== null) twTitle.setAttribute('content', prevTwTitle);
      if (prevTwDesc !== null) twDesc.setAttribute('content', prevTwDesc);
      if (prevTwImage !== null) twImage.setAttribute('content', prevTwImage);
      if (metaRobots && prevRobots !== null) metaRobots.setAttribute('content', prevRobots);
    };
  }, [meta.title, meta.description, meta.image, meta.url, meta.type, meta.robots]);
}
